import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { formatBytes } from '../board/items/FileItem';
import {
  createAdminInvite,
  deleteUser,
  loadAdmin,
  revokeInvite,
  updateUser,
  type AdminInvite,
  type AdminState,
  type AdminUser,
  type InviteGrants,
  type UserPatch,
} from '../sync/adminApi';
import { logout, useAuth } from '../sync/authStore';
import { paths } from './router';

const MB = 1024 * 1024;

/** Accounts, their storage allowances and permissions, and server-wide invites. Admins only. */
export function AdminPage() {
  const me = useAuth((s) => s.user);
  const [state, setState] = useState<AdminState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setState(await loadAdmin());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /** Runs a change, shows its failure, and reloads the lists afterwards. */
  const run = async (fn: () => Promise<unknown>): Promise<boolean> => {
    try {
      await fn();
      setError(null);
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return false;
    } finally {
      await refresh();
    }
  };

  const save = async (u: AdminUser, patch: UserPatch) => {
    if (await run(() => updateUser(u.id, patch))) setEditing(null);
  };

  const remove = (u: AdminUser) => {
    const what = `${u.name} (${u.email}), ${u.boards} ${u.boards === 1 ? 'board' : 'boards'} and ${formatBytes(u.usedBytes)} of files`;
    if (!confirm(`Delete ${what}?\n\nBoards shared with this account stay with their owners. This cannot be undone.`)) return;
    void run(() => deleteUser(u.id));
  };

  const invite = async (input: { note: string; grants: InviteGrants; expiresDays: number }) => {
    const ok = await run(async () => {
      const inv = await createAdminInvite(input);
      const url = inviteLink(inv.code);
      setInviteUrl(url);
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        /* clipboard may be unavailable; the link is shown anyway */
      }
    });
    return ok;
  };

  const revoke = (inv: AdminInvite) => {
    if (!confirm(`Revoke the invite${inv.note ? ` for "${inv.note}"` : ''}? The link stops working.`)) return;
    void run(() => revokeInvite(inv.code));
  };

  const users = state?.users ?? [];
  const admins = users.filter((u) => u.isAdmin && !u.disabled).length;
  const suspended = users.filter((u) => u.disabled).length;
  const server = state?.server;

  return (
    <main className="page admin-page">
      <header className="page-header">
        <h1 title="Visual Data Slate">VDS · Admin</h1>
        <div className="page-header-right">
          <span className="muted">{me?.name}</span>
          <a className="btn" href={paths.dashboard}>
            Boards
          </a>
          <button type="button" className="btn" onClick={() => void logout()}>
            Sign out
          </button>
        </div>
      </header>

      {error && <div className="form-error admin-error">{error}</div>}
      {state === null && !error && <p className="muted">Loading…</p>}

      {server && (
        <section className="win">
          <div className="win-title">
            <span className="win-title-text">Server</span>
          </div>
          <div className="win-body">
            <dl className="kv admin-kv">
              <dt>Accounts</dt>
              <dd>
                {users.length} · {admins} {admins === 1 ? 'admin' : 'admins'}
                {suspended ? ` · ${suspended} suspended` : ''}
              </dd>
              <dt>Files stored</dt>
              <dd>{formatBytes(server.usedBytes)} across every account (identical files counted once per owner)</dd>
              <dt>Disk</dt>
              <dd>
                {server.diskFreeBytes !== null && server.diskTotalBytes !== null
                  ? `${formatBytes(server.diskFreeBytes)} free of ${formatBytes(server.diskTotalBytes)}`
                  : 'unknown'}
              </dd>
              <dt>Default allowance</dt>
              <dd>
                {quotaLabel(server.defaultQuotaBytes)} <span className="muted">(DEFAULT_QUOTA_MB in .env; applies to accounts without their own)</span>
              </dd>
              <dt>Max upload</dt>
              <dd>{formatBytes(server.maxUploadBytes)} per file</dd>
            </dl>
          </div>
        </section>
      )}

      {state && (
        <section className="win">
          <div className="win-title">
            <span className="win-title-text">Accounts</span>
            <span className="win-title-opt">{users.length}</span>
          </div>
          <div className="win-body">
            <div className="table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Account</th>
                    <th>Role</th>
                    <th>Storage</th>
                    <th className="num">Boards</th>
                    <th>Invited by</th>
                    <th>Joined</th>
                    <th>Last sign-in</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <UserRows
                      key={u.id}
                      user={u}
                      isMe={u.id === me?.id}
                      defaultQuota={state.server.defaultQuotaBytes}
                      editing={editing === u.id}
                      onEdit={() => setEditing((cur) => (cur === u.id ? null : u.id))}
                      onCancel={() => setEditing(null)}
                      onSave={(patch) => void save(u, patch)}
                      onDelete={() => remove(u)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <p className="muted admin-hint">
              Storage counts the files on the boards an account owns; uploads by editors on a shared board are charged to its owner. A suspended
              account cannot sign in and its open sessions end.
            </p>
          </div>
        </section>
      )}

      {state && (
        <section className="win">
          <div className="win-title">
            <span className="win-title-text">Invites</span>
            <span className="win-title-opt">{state.invites.length} open</span>
          </div>
          <div className="win-body">
            <InviteForm defaultQuota={state.server.defaultQuotaBytes} onCreate={invite} />
            {inviteUrl && (
              <div className="notice">
                <span>Invite link (one use), copied to clipboard:</span>
                <code>{inviteUrl}</code>
                <button type="button" className="btn" onClick={() => setInviteUrl(null)}>
                  Dismiss
                </button>
              </div>
            )}
            {state.invites.length === 0 ? (
              <p className="muted dash-empty">No open invites.</p>
            ) : (
              <div className="table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>For</th>
                      <th>Presets</th>
                      <th>Created by</th>
                      <th>Expires</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {state.invites.map((inv) => (
                      <tr key={inv.code}>
                        <td>
                          <div className="admin-name">{inv.note || <span className="muted">(no note)</span>}</div>
                          <div className="muted admin-sub">
                            <code className="admin-code" title={inv.code}>
                              {inv.code}
                            </code>
                          </div>
                        </td>
                        <td>{grantsLabel(inv.grants, state.server.defaultQuotaBytes)}</td>
                        <td>{inv.createdBy ?? '—'}</td>
                        <td>{formatDate(inv.expiresAt)}</td>
                        <td className="admin-actions">
                          <button type="button" className="btn btn-small" onClick={() => void copy(inviteLink(inv.code))}>
                            Copy link
                          </button>
                          <button type="button" className="btn btn-small danger" onClick={() => revoke(inv)}>
                            Revoke
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}
    </main>
  );
}

/** One account: its summary row and, while editing, a second row with the form. */
function UserRows({
  user: u,
  isMe,
  defaultQuota,
  editing,
  onEdit,
  onCancel,
  onSave,
  onDelete,
}: {
  user: AdminUser;
  isMe: boolean;
  defaultQuota: number;
  editing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: (patch: UserPatch) => void;
  onDelete: () => void;
}) {
  const quota = u.effectiveQuotaBytes;
  const ratio = quota > 0 ? u.usedBytes / quota : 0;
  const barClass = ratio >= 1 ? ' is-over' : ratio >= 0.9 ? ' is-warn' : '';
  const flags: string[] = [];
  if (u.disabled) flags.push('suspended');
  if (!u.canInvite) flags.push('no invites');
  if (!u.canShare) flags.push('no share links');
  return (
    <>
      <tr className={`${u.disabled ? 'is-suspended' : ''}${editing ? ' is-editing' : ''}`}>
        <td>
          <div className="admin-name">
            {u.name}
            {isMe && <span className="tag admin-tag">you</span>}
          </div>
          <div className="muted admin-sub">{u.email}</div>
          {flags.length > 0 && <div className="admin-flags">{flags.join(' · ')}</div>}
        </td>
        <td>{u.isAdmin ? 'admin' : 'member'}</td>
        <td className="admin-storage">
          <div>
            {formatBytes(u.usedBytes)}
            {quota > 0 ? ` of ${formatBytes(quota)}` : ''}
            {u.quotaBytes === null && <span className="muted"> · default</span>}
            {quota === 0 && <span className="muted"> · unlimited</span>}
          </div>
          {quota > 0 && (
            <div className={`quota-bar${barClass}`} title={`${Math.round(ratio * 100)}% used`}>
              <i style={{ width: `${Math.min(100, ratio * 100)}%` }} />
            </div>
          )}
        </td>
        <td className="num">{u.boards}</td>
        <td>{u.invitedBy ?? (u.isAdmin ? '—' : '?')}</td>
        <td>{formatDate(u.createdAt)}</td>
        <td>{u.lastLoginAt ? formatDate(u.lastLoginAt) : 'never'}</td>
        <td className="admin-actions">
          <button type="button" className={`btn btn-small${editing ? ' is-active' : ''}`} onClick={onEdit}>
            {editing ? 'Close' : 'Edit'}
          </button>
        </td>
      </tr>
      {editing && (
        <tr className="admin-editor-row">
          <td colSpan={8}>
            <UserEditor user={u} isMe={isMe} defaultQuota={defaultQuota} onCancel={onCancel} onSave={onSave} onDelete={onDelete} />
          </td>
        </tr>
      )}
    </>
  );
}

function UserEditor({
  user: u,
  isMe,
  defaultQuota,
  onCancel,
  onSave,
  onDelete,
}: {
  user: AdminUser;
  isMe: boolean;
  defaultQuota: number;
  onCancel: () => void;
  onSave: (patch: UserPatch) => void;
  onDelete: () => void;
}) {
  const [name, setName] = useState(u.name);
  const [role, setRole] = useState(u.role);
  const [quota, setQuota] = useState<number | null>(u.quotaBytes);
  const [canInvite, setCanInvite] = useState(u.canInvite);
  const [canShare, setCanShare] = useState(u.canShare);
  const [disabled, setDisabled] = useState(u.disabled);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const patch: UserPatch = {};
    if (name.trim() !== u.name) patch.name = name.trim();
    if (role !== u.role) patch.role = role;
    if (quota !== u.quotaBytes) patch.quotaBytes = quota;
    if (canInvite !== u.canInvite) patch.canInvite = canInvite;
    if (canShare !== u.canShare) patch.canShare = canShare;
    if (disabled !== u.disabled) patch.disabled = disabled;
    if (Object.keys(patch).length === 0) {
      onCancel();
      return;
    }
    onSave(patch);
  };

  return (
    <form className="admin-editor" onSubmit={submit}>
      <label className="admin-field">
        <span>Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={100} required />
      </label>
      <label className="admin-field">
        <span>Role</span>
        <select value={role} onChange={(e) => setRole(e.target.value as AdminUser['role'])} disabled={isMe}>
          <option value="member">member</option>
          <option value="admin">admin</option>
        </select>
      </label>
      <AllowanceField label="Storage allowance" value={quota} defaultQuota={defaultQuota} onChange={setQuota} />
      <div className="admin-field admin-checks">
        <span>Permissions</span>
        <label className="opt-check">
          <input type="checkbox" checked={canInvite} onChange={(e) => setCanInvite(e.target.checked)} />
          Can invite others
        </label>
        <label className="opt-check">
          <input type="checkbox" checked={canShare} onChange={(e) => setCanShare(e.target.checked)} />
          Can create share links
        </label>
        <label className="opt-check" title={isMe ? 'You cannot suspend yourself' : 'Cannot sign in; open sessions end'}>
          <input type="checkbox" checked={disabled} disabled={isMe} onChange={(e) => setDisabled(e.target.checked)} />
          Suspended
        </label>
      </div>
      <div className="admin-editor-actions">
        <button type="submit" className="btn btn-primary">
          Save
        </button>
        <button type="button" className="btn" onClick={onCancel}>
          Cancel
        </button>
        {!isMe && (
          <button type="button" className="btn danger admin-delete" onClick={onDelete}>
            Delete account…
          </button>
        )}
      </div>
    </form>
  );
}

/** New invite with presets for the account it will create. */
function InviteForm({
  defaultQuota,
  onCreate,
}: {
  defaultQuota: number;
  onCreate: (input: { note: string; grants: InviteGrants; expiresDays: number }) => Promise<boolean>;
}) {
  const [note, setNote] = useState('');
  const [quota, setQuota] = useState<number | null>(null);
  const [canInvite, setCanInvite] = useState(false);
  const [canShare, setCanShare] = useState(true);
  const [days, setDays] = useState(7);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const grants: InviteGrants = { canInvite, canShare };
      if (quota !== null) grants.quotaBytes = quota;
      if (await onCreate({ note: note.trim(), grants, expiresDays: days })) setNote('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="admin-editor admin-invite-form" onSubmit={submit}>
      <label className="admin-field">
        <span>For (note)</span>
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="who this invite is for" maxLength={120} />
      </label>
      <AllowanceField label="Storage allowance" value={quota} defaultQuota={defaultQuota} onChange={setQuota} />
      <div className="admin-field admin-checks">
        <span>Permissions</span>
        <label className="opt-check">
          <input type="checkbox" checked={canInvite} onChange={(e) => setCanInvite(e.target.checked)} />
          Can invite others
        </label>
        <label className="opt-check">
          <input type="checkbox" checked={canShare} onChange={(e) => setCanShare(e.target.checked)} />
          Can create share links
        </label>
      </div>
      <label className="admin-field admin-days">
        <span>Valid for</span>
        <span className="admin-inline">
          <input type="number" min={1} max={90} value={days} onChange={(e) => setDays(Math.max(1, Math.min(90, Number(e.target.value) || 7)))} />
          days
        </span>
      </label>
      <div className="admin-editor-actions">
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? 'Creating…' : 'New invite'}
        </button>
      </div>
    </form>
  );
}

/**
 * Allowance picker. null = the server default, 0 = unlimited, otherwise bytes (entered in MB).
 * The custom amount is kept while the mode changes so switching back does not lose it.
 */
function AllowanceField({
  label,
  value,
  defaultQuota,
  onChange,
}: {
  label: string;
  value: number | null;
  defaultQuota: number;
  onChange: (v: number | null) => void;
}) {
  const mode = value === null ? 'default' : value === 0 ? 'unlimited' : 'custom';
  const [mb, setMb] = useState(value && value > 0 ? Math.round(value / MB) : 1024);

  const setMode = (m: string) => {
    if (m === 'default') onChange(null);
    else if (m === 'unlimited') onChange(0);
    else onChange(Math.max(1, mb) * MB);
  };

  return (
    <div className="admin-field">
      <span>{label}</span>
      <span className="admin-inline">
        <select value={mode} onChange={(e) => setMode(e.target.value)}>
          <option value="default">Server default ({quotaLabel(defaultQuota)})</option>
          <option value="unlimited">Unlimited</option>
          <option value="custom">Custom</option>
        </select>
        {mode === 'custom' && (
          <>
            <input
              type="number"
              min={1}
              step={1}
              value={mb}
              onChange={(e) => {
                const v = Math.max(1, Math.floor(Number(e.target.value) || 1));
                setMb(v);
                onChange(v * MB);
              }}
            />
            MB
          </>
        )}
      </span>
    </div>
  );
}

function quotaLabel(bytes: number): string {
  return bytes > 0 ? formatBytes(bytes) : 'unlimited';
}

function grantsLabel(g: InviteGrants, defaultQuota: number): string {
  const parts: string[] = [];
  parts.push(g.quotaBytes === undefined || g.quotaBytes === null ? `default allowance (${quotaLabel(defaultQuota)})` : quotaLabel(g.quotaBytes));
  if (g.canInvite === false) parts.push('no invites');
  if (g.canShare === false) parts.push('no share links');
  return parts.join(' · ');
}

function inviteLink(code: string): string {
  return `${window.location.origin}${window.location.pathname}${paths.register(code)}`;
}

async function copy(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    prompt('Copy this link', text);
  }
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
